import { Cause, Effect, Queue, Schedule, type Scope, Stream } from "effect";
import type * as Arrows from "../game/arrows.ts";
import * as Song from "../game/song.ts";
import * as Chart from "../shared/chart.ts";
import * as Errors from "../shared/errors.ts";
import * as Render from "./render.ts";
import * as Session from "./session.ts";
import * as Sprites from "../dig/sprites.ts";

// A dropped connection is tried again every second: the server is one origin and either answers
// or does not; nothing is gained by backing off from it.
const RECONNECT = "1 second";

const canvas = document.createElement("canvas");
document.body.append(canvas);

// What the render loop paints; the session replaces it whenever the world or the clock changes.
let current: Session.View = Session.WAITING;

// A browser keeps a context silent until the page is clicked or a key is pressed; until then the
// frame asks for that click and the speaker refuses every batch. resume settles into the
// context's state, which is read where it matters; its promise says nothing more.
const audio = new AudioContext();
const unlock = (): void => {
  void audio.resume();
};
window.addEventListener("click", unlock);
window.addEventListener("keydown", unlock);

// Every key the page is given, for whichever session is running; unbounded because a person
// presses a few keys a second and the session takes each as it comes. A key held down repeats,
// which would swing again the moment a swing ends; a key with a modifier is the browser's or the
// system's (copy, a new tab), not the game's. The keys the game reads are kept from the browser
// too, so a letter of a name does not open a quick find.
const keys = Effect.runSync(Queue.unbounded<string>());
window.addEventListener("keydown", (event) => {
  if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) {
    return;
  }
  if (event.key.length === 1 || event.key.startsWith("Arrow") || event.key === "Backspace") {
    event.preventDefault();
  }
  Queue.offerUnsafe(keys, event.key);
});

// Every voice meets at one compressor: the drop has five sounding at once, and a bare
// destination would clip.
const mix = (): DynamicsCompressorNode => {
  const compressor = audio.createDynamicsCompressor();
  compressor.threshold.value = -14;
  compressor.knee.value = 12;
  compressor.ratio.value = 6;
  compressor.attack.value = 0.003;
  compressor.release.value = 0.15;
  compressor.connect(audio.destination);
  return compressor;
};
let master = mix();

// The server time up to which the song has been placed on the output clock.
let placed = 0;

// Up to two seconds of sound are queued at any moment. Cutting them leaves the queue in a mix
// nothing hears, and drops the mark so the song is placed again from the present.
const silence = (): void => {
  master.disconnect();
  master = mix();
  placed = 0;
};

// An interruption (a call, another app taking the output on a phone) pauses the context with the
// queue still in it, which would play over what is placed after it resumes.
audio.addEventListener("statechange", () => {
  if (audio.state !== "running") {
    silence();
  }
});

// A second of white noise for the clap and the hats to shape.
const noise = audio.createBuffer(1, audio.sampleRate, audio.sampleRate);
const samples = noise.getChannelData(0);
for (let i = 0; i < samples.length; i++) {
  samples[i] = Math.random() * 2 - 1;
}

// A gain into the master that opens to `peak` at `when` and dies away over `decay` seconds. The
// ramps run from and to a hair above zero because an exponential ramp cannot touch it.
const envelope = (when: number, peak: number, decay: number): GainNode => {
  const gain = audio.createGain();
  gain.gain.setValueAtTime(0.0001, when);
  gain.gain.linearRampToValueAtTime(peak, when + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.0001, when + decay);
  gain.connect(master);
  return gain;
};

const burst = (
  when: number,
  peak: number,
  decay: number,
  type: BiquadFilterType,
  hz: number,
): void => {
  const source = audio.createBufferSource();
  source.buffer = noise;
  const filter = audio.createBiquadFilter();
  filter.type = type;
  filter.frequency.value = hz;
  source.connect(filter).connect(envelope(when, peak, decay));
  source.start(when);
  source.stop(when + decay);
};

// One note, at `when` seconds on the output clock. Every node here is released once it has
// stopped: nothing holds it but the graph, and the graph lets a finished source go.
const sound = (note: Song.Note, when: number): void => {
  switch (note.voice) {
    case "kick": {
      // A sine falling from 150 to 45 Hz in its first tenth of a second is the thump.
      const osc = audio.createOscillator();
      osc.frequency.setValueAtTime(150, when);
      osc.frequency.exponentialRampToValueAtTime(45, when + 0.1);
      osc.connect(envelope(when, 1, 0.4));
      osc.start(when);
      osc.stop(when + 0.4);
      return;
    }
    case "clap":
      burst(when, 0.6, 0.18, "bandpass", 1_800);
      return;
    case "hat":
      burst(when, note.open ? 0.28 : 0.18, note.open ? 0.3 : 0.06, "highpass", 7_000);
      return;
    case "bass": {
      // A sawtooth under a low-pass that snaps shut is the pluck.
      const osc = audio.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.value = note.hz;
      const filter = audio.createBiquadFilter();
      filter.type = "lowpass";
      filter.Q.value = 6;
      filter.frequency.setValueAtTime(900, when);
      filter.frequency.exponentialRampToValueAtTime(110, when + 0.16);
      osc.connect(filter).connect(envelope(when, 0.5, 0.26));
      osc.start(when);
      osc.stop(when + 0.26);
      return;
    }
    case "lead": {
      // Two sawtooths seven cents either side of the pitch, under a gentler low-pass.
      const filter = audio.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 2_400;
      filter.connect(envelope(when, 0.17, 0.32));
      for (const detune of [-7, 7]) {
        const osc = audio.createOscillator();
        osc.type = "sawtooth";
        osc.frequency.value = note.hz;
        osc.detune.value = detune;
        osc.connect(filter);
        osc.start(when);
        osc.stop(when + 0.32);
      }
      return;
    }
    default:
      return note satisfies never;
  }
};

// Places a batch on the output clock. The two clocks are read together: the server's, as this
// machine estimates it, and the context's. currentTime moves in render quanta of under three
// milliseconds, which is the most the pairing is off by, and pairing them anew for every batch
// means nothing drifts. Two tabs on one machine read the same Date.now and the same device, so
// they land within that of each other. A note the tick has just passed sounds now, late by a
// few milliseconds at most; the context also refuses a time before its own zero, which right
// after unlocking such a note would be.
const speaker = Session.Speaker.of({
  placed: () => placed,
  play: (notes, until, offset) => {
    if (audio.state !== "running") {
      return;
    }
    const serverNow = Date.now() + offset;
    const contextNow = audio.currentTime;
    for (const note of notes) {
      sound(note, Math.max(contextNow, contextNow + (note.at - serverNow) / 1_000));
    }
    placed = Math.max(placed, until);
  },
  silence,
});

// A quarter turn clockwise per direction from up, which is how the arrow path below is drawn.
const TURN: Record<Arrows.Direction, number> = {
  up: 0,
  right: Math.PI / 2,
  down: Math.PI,
  left: -Math.PI / 2,
};

// Each sprite is painted once, mark by mark, onto a small canvas of its own, and stamped from
// there scaled up. A browser that gave the page a context gives one to these too.
const stamps = new WeakMap<Sprites.Sprite, HTMLCanvasElement>();
const stamp = (sprite: Sprites.Sprite): HTMLCanvasElement => {
  const known = stamps.get(sprite);
  if (known !== undefined) {
    return known;
  }
  const small = document.createElement("canvas");
  small.width = Sprites.SIZE;
  small.height = Sprites.SIZE;
  const g = small.getContext("2d");
  if (g === null) {
    throw new Error("a canvas that painted the page cannot paint a sprite");
  }
  sprite.rows.forEach((row, y) => {
    row.split("").forEach((mark, x) => {
      const color = sprite.palette[mark];
      if (color !== undefined) {
        g.fillStyle = color;
        g.fillRect(x, y, 1, 1);
      }
    });
  });
  stamps.set(sprite, small);
  return small;
};

const paint = (ctx: CanvasRenderingContext2D, op: Render.Op): void => {
  switch (op.kind) {
    case "clear":
      ctx.fillStyle = op.color;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      return;
    case "rect":
      ctx.fillStyle = op.color;
      ctx.fillRect(op.x, op.y, op.width, op.height);
      return;
    case "arrow":
      // A triangular head over a stem, in a unit square about the centre, turned to its direction.
      ctx.fillStyle = op.color;
      ctx.save();
      ctx.translate(op.x, op.y);
      ctx.rotate(TURN[op.direction]);
      ctx.scale(op.size, op.size);
      ctx.beginPath();
      ctx.moveTo(0, -0.5);
      ctx.lineTo(0.5, 0);
      ctx.lineTo(0.22, 0);
      ctx.lineTo(0.22, 0.5);
      ctx.lineTo(-0.22, 0.5);
      ctx.lineTo(-0.22, 0);
      ctx.lineTo(-0.5, 0);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      return;
    case "text":
      ctx.fillStyle = op.color;
      ctx.font = `${String(op.size)}px system-ui, sans-serif`;
      ctx.textAlign = op.align;
      ctx.textBaseline = "alphabetic";
      ctx.fillText(op.text, op.x, op.y);
      return;
    case "sprite":
      ctx.imageSmoothingEnabled = false;
      if (op.ghost === true) {
        ctx.globalAlpha = 0.75;
      }
      ctx.drawImage(stamp(op.sprite), op.x, op.y, Sprites.SIZE * op.scale, Sprites.SIZE * op.scale);
      ctx.globalAlpha = 1;
      return;
    default:
      return op satisfies never;
  }
};

// Every frame: size the bitmap to the window at device resolution, then paint the ops for now.
const animate = (ctx: CanvasRenderingContext2D, chart: Arrows.Chart): void => {
  const loop = (): void => {
    const ratio = window.devicePixelRatio;
    const width = Math.round(canvas.clientWidth * ratio);
    const height = Math.round(canvas.clientHeight * ratio);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    const scene: Render.Scene = {
      view: current,
      chart,
      clientNow: Date.now(),
      audible: audio.state === "running",
    };
    for (const op of Render.ops({ width, height }, scene)) {
      paint(ctx, op);
    }
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);
};

// The chart is a file beside the page, fetched once as the page starts.
const loadChart: Effect.Effect<Chart.Chart, Errors.BadChart> = Effect.gen(function* () {
  const response = yield* Effect.tryPromise({
    try: () => fetch("/chart.json"),
    catch: (cause) => Errors.BadChart.make({ reason: "could not be fetched", cause }),
  });
  if (!response.ok) {
    return yield* Errors.BadChart.make({
      reason: `the server answered ${String(response.status)}`,
      cause: response.statusText,
    });
  }
  const text = yield* Effect.tryPromise({
    try: () => response.text(),
    catch: (cause) => Errors.BadChart.make({ reason: "could not be read", cause }),
  });
  return yield* Chart.decodeChart(text);
});

const disconnected = (event: CloseEvent): Errors.Disconnected =>
  Errors.Disconnected.make({ code: event.code, reason: event.reason });

// Opens the socket and hands the session its two halves. The inbox is filled from the moment the
// socket exists, so the world the room sends on accept is never missed; it is unbounded because
// the session drains it as fast as frames arrive and the room sends a few small ones a second.
const connect = (url: string): Effect.Effect<Session.LinkShape, Errors.Disconnected, Scope.Scope> =>
  Effect.gen(function* () {
    const inbox = yield* Queue.unbounded<Session.Frame, Errors.Disconnected | Errors.BadMessage>();
    const socket = yield* Effect.acquireRelease(
      Effect.sync(() => {
        const ws = new WebSocket(url);
        // Binary frames arrive as ArrayBuffer, not Blob, so a frame is a string or an ArrayBuffer
        // here as it is in the room.
        ws.binaryType = "arraybuffer";
        ws.addEventListener("message", (event: MessageEvent<string | ArrayBuffer>) => {
          if (typeof event.data === "string") {
            Queue.offerUnsafe(inbox, { text: event.data, at: Date.now() });
          } else {
            Queue.failCauseUnsafe(
              inbox,
              Cause.fail(Errors.BadMessage.make({ text: "<binary frame>" })),
            );
          }
        });
        ws.addEventListener("close", (event) => {
          Queue.failCauseUnsafe(inbox, Cause.fail(disconnected(event)));
        });
        return ws;
      }),
      (ws) => Effect.sync(() => ws.close()),
    );
    // Open, or closed before it opened (refused, unreachable): whichever comes first decides,
    // and the other listener goes with it.
    yield* Effect.callback<void, Errors.Disconnected>((resume) => {
      const settled = new AbortController();
      const options = { once: true, signal: settled.signal };
      socket.addEventListener(
        "open",
        () => {
          settled.abort();
          resume(Effect.void);
        },
        options,
      );
      socket.addEventListener(
        "close",
        (event) => {
          settled.abort();
          resume(Effect.fail(disconnected(event)));
        },
        options,
      );
    });
    return Session.Link.of({
      send: (text) =>
        Effect.try({
          try: () => socket.send(text),
          catch: (cause) => Errors.SendFailed.make({ message: "websocket send failed", cause }),
        }),
      messages: Stream.fromQueue(inbox),
    });
  });

const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;

const report = (text: string): Effect.Effect<void> => Effect.sync(() => console.warn(text));

// Connect, run the session until it ends, say why, and go again a second later. A message this
// client cannot read means the server is newer than this page: reload to get the matching client.
const sessions = Effect.scoped(
  Effect.gen(function* () {
    const link = yield* connect(url);
    yield* Effect.provideService(Session.session, Session.Link, link);
  }),
).pipe(
  Effect.catchTag("Disconnected", (error) => report(`${error.message}; reconnecting`)),
  Effect.catchTag("SendFailed", (error) => report(`${error.message}; reconnecting`)),
  Effect.repeat(Schedule.spaced(RECONNECT)),
  Effect.catchTag("BadMessage", (error) =>
    Effect.sync(() => {
      console.error(`${error.message}; reloading`);
      location.reload();
    }),
  ),
  Effect.provideService(
    Session.Screen,
    Session.Screen.of({
      show: (view) => {
        current = view;
      },
    }),
  ),
  Effect.provideService(Session.Speaker, speaker),
  Effect.provideService(Session.Controls, Session.Controls.of({ keys: Stream.fromQueue(keys) })),
);

// The page in place of the game, when it cannot run.
const say = (message: string): void => {
  const note = document.createElement("p");
  note.textContent = message;
  document.body.replaceChildren(note);
};

// The chart first, then the frames and the room. A chart the page cannot read is a file someone
// is editing: the page says what is wrong with it, and nothing else, until it is fixed.
const program = (ctx: CanvasRenderingContext2D): Effect.Effect<void> =>
  Effect.gen(function* () {
    const chart = yield* loadChart;
    animate(ctx, chart);
    yield* sessions;
  }).pipe(Effect.catchTag("BadChart", (error) => Effect.sync(() => say(error.message))));

const context = canvas.getContext("2d");
if (context === null) {
  say("This browser cannot draw on a canvas.");
} else {
  Effect.runFork(program(context));
}
